import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Users, DollarSign, Video, TrendingUp, 
  CheckCircle, Clock, Music, Mail, Phone,
  Shield, Ban, Eye, Loader2, Globe
} from 'lucide-react';

// Liste des pays
const COUNTRIES = [
  { code: 'CD', name: 'RD Congo 🇨🇩' },
  { code: 'FR', name: 'France 🇫🇷' },
  { code: 'BE', name: 'Belgique 🇧🇪' },
  { code: 'CA', name: 'Canada 🇨🇦' },
  { code: 'US', name: 'États-Unis 🇺🇸' },
  { code: 'CM', name: 'Cameroun 🇨🇲' },
  { code: 'CI', name: 'Côte d\'Ivoire 🇨🇮' },
  { code: 'SN', name: 'Sénégal 🇸🇳' },
  { code: 'GA', name: 'Gabon 🇬🇦' },
  { code: 'ML', name: 'Mali 🇲🇱' },
  { code: 'BF', name: 'Burkina Faso 🇧🇫' },
  { code: 'NE', name: 'Niger 🇳🇪' },
  { code: 'TG', name: 'Togo 🇹🇬' },
  { code: 'BJ', name: 'Bénin 🇧🇯' },
  { code: 'CH', name: 'Suisse 🇨🇭' },
  { code: 'GB', name: 'Royaume-Uni 🇬🇧' },
  { code: 'DE', name: 'Allemagne 🇩🇪' }
];

const AdminPanel = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [titles, setTitles] = useState([]);
  const [artists, setArtists] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [liveQueue, setLiveQueue] = useState([]);
  const [adminTeam, setAdminTeam] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  
  // Live Studio
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [uploadVideoTitle, setUploadVideoTitle] = useState('');
  const [uploadVideoFile, setUploadVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  // Team Management
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPhone, setNewAdminPhone] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Nouveaux états pour les 10 points
  const [globalSettings, setGlobalSettings] = useState(null);
  const [contactMessages, setContactMessages] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // AGRP BOX TV Manuelle
  const [agrpBoxTvTitle, setAgrpBoxTvTitle] = useState('');
  const [agrpBoxTvFile, setAgrpBoxTvFile] = useState(null);
  const [uploadingAgrpBoxTv, setUploadingAgrpBoxTv] = useState(false);
  const [readyVideos, setReadyVideos] = useState([]);
  const [currentLiveStatus, setCurrentLiveStatus] = useState(null);
  const [launchingLive, setLaunchingLive] = useState(false);
  
  // GESTION ADMIN - Toutes les vidéos
  const [allVideos, setAllVideos] = useState([]);
  
  // GESTION DES PAYS
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [savingCountries, setSavingCountries] = useState(false);
  
  // DEMANDES ADSENSE
  const [adSenseApplications, setAdSenseApplications] = useState([]);
  const [reviewingAdSense, setReviewingAdSense] = useState(false);
  
  // COMMANDES SERVICES
  const [serviceOrders, setServiceOrders] = useState([]);
  const [updatingOrder, setUpdatingOrder] = useState(false);
  
  // NOTIFICATIONS
  const [notificationsCount, setNotificationsCount] = useState(0);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Vérifier si l'utilisateur est Super Admin
    if (user?.role !== 'super_admin') {
      toast.error('Accès refusé : Super Admin uniquement');
      navigate('/');
      return;
    }
    
    fetchAllData();
  }, [isAuthenticated, user]);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Charger les données de base
      const statsRes = await api.get('/admin/dashboard-stats').catch(() => ({ data: {} }));
      const titlesRes = await api.get('/admin/titles').catch(() => ({ data: [] }));
      const artistsRes = await api.get('/admin/artists').catch(() => ({ data: [] }));
      const transactionsRes = await api.get('/admin/transactions').catch(() => ({ data: [] }));
      const liveQueueRes = await api.get('/admin/live/queue').catch(() => ({ data: [] }));
      const teamRes = await api.get('/admin/team').catch(() => ({ data: [] }));
      const settingsRes = await api.get('/admin/global-settings').catch(() => ({ data: {} }));
      const messagesRes = await api.get('/admin/contact-messages').catch(() => ({ data: { messages: [] } }));
      const readyVideosRes = await api.get('/admin/dyvy-tv/ready-videos').catch(() => ({ data: { videos: [] } }));
      const liveStatusRes = await api.get('/admin/dyvy-tv/current-status').catch(() => ({ data: {} }));
      
      // Charger les vidéos de la boutique (pour GESTION ADMIN)
      const boutiqueRes = await api.get('/boutique').catch(() => ({ data: [] }));
      
      // Charger les demandes AdSense
      const adSenseRes = await api.get('/admin/adsense/applications').catch(() => ({ data: { applications: [] } }));
      
      // Charger les commandes de services
      const servicesRes = await api.get('/admin/creator-services/orders').catch(() => ({ data: { orders: [] } }));
      
      // Charger le compteur de notifications
      const notifRes = await api.get('/admin/notifications/count').catch(() => ({ data: { total_pending: 0 } }));
      
      setStats(statsRes.data || {});
      setTitles(titlesRes.data || []);
      setArtists(artistsRes.data || []);
      setTransactions(transactionsRes.data || []);
      setLiveQueue(liveQueueRes.data || []);
      setAdminTeam(teamRes.data || []);
      setGlobalSettings(settingsRes.data || {});
      setContactMessages(messagesRes.data.messages || []);
      setReadyVideos(readyVideosRes.data.videos || []);
      setCurrentLiveStatus(liveStatusRes.data || {});
      setAllVideos(boutiqueRes.data || []);
      setAdSenseApplications(adSenseRes.data.applications || []);
      setServiceOrders(servicesRes.data.orders || []);
      setNotificationsCount(notifRes.data.total_pending || 0);      
      console.log('✅ Toutes les données chargées', { totalVideos: boutiqueRes.data?.length || 0 });
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnabledCountries = async () => {
    try {
      const res = await api.get('/admin/countries/enabled');
      setEnabledCountries(res.data.enabled_countries || []);
    } catch (error) {
      console.error('Erreur chargement pays:', error);
    }
  };
  
  const handleToggleCountry = (countryCode) => {
    if (enabledCountries.includes(countryCode)) {
      setEnabledCountries(enabledCountries.filter(c => c !== countryCode));
    } else {
      setEnabledCountries([...enabledCountries, countryCode]);
    }
  };
  
  const handleSaveCountries = async () => {
    setSavingCountries(true);
    try {
      await api.put('/admin/countries/enabled', enabledCountries);
      toast.success(`✅ ${enabledCountries.length} pays autorisés`);
    } catch (error) {
      toast.error('Erreur sauvegarde pays');
    } finally {
      setSavingCountries(false);
    }
  };
  
  const handleReviewAdSense = async (userId, status, note = '') => {
    setReviewingAdSense(true);
    try {
      await api.put(`/admin/adsense/applications/${userId}/status`, {
        status,
        admin_note: note
      });
      toast.success(`✅ Statut mis à jour: ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur de mise à jour');
    } finally {
      setReviewingAdSense(false);
    }
  };
  
  const handleUpdateServiceOrder = async (userId, status, note = '') => {
    setUpdatingOrder(true);
    try {
      await api.put(`/admin/creator-services/orders/${userId}/status`, {
        delivery_status: status,
        admin_note: note
      });
      toast.success(`✅ Statut mis à jour: ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur de mise à jour');
    } finally {
      setUpdatingOrder(false);
    }
  };
  
  const handleValidateTitle = async (titleId) => {
    try {
      await api.post(`/admin/titles/${titleId}/validate`);
      toast.success('✅ Titre validé et envoyé à DistroKid');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur de validation');
    }
  };
  
  const handleToggleArtistStatus = async (artistId) => {
    try {
      const res = await api.post(`/admin/artists/${artistId}/toggle-status`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur de modification');
    }
  };
  
  // Live Studio Functions
  const handleAddYoutube = async () => {
    if (!youtubeUrl) {
      toast.error('URL YouTube requise');
      return;
    }
    
    try {
      await api.post('/admin/live/add-youtube', {
        url: youtubeUrl,
        title: youtubeTitle || 'Vidéo YouTube'
      });
      toast.success('✅ Vidéo YouTube ajoutée');
      setYoutubeUrl('');
      setYoutubeTitle('');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur ajout YouTube');
    }
  };
  
  const handleUploadVideo = async () => {
    if (!uploadVideoFile || !uploadVideoTitle) {
      toast.error('Titre et fichier vidéo requis');
      return;
    }
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadVideoTitle);
      formData.append('video_file', uploadVideoFile);
      
      await api.post('/admin/live/upload-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('✅ Vidéo uploadée');
      setUploadVideoTitle('');
      setUploadVideoFile(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur upload vidéo');
    } finally {
      setUploadingVideo(false);
    }
  };
  
  const handleLaunchLive = async (videoId) => {
    try {
      const res = await api.post(`/admin/live/${videoId}/launch`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lancement live');
    }
  };
  
  const handleDeleteLiveVideo = async (videoId) => {
    try {
      await api.delete(`/admin/live/${videoId}`);
      toast.success('Vidéo supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };
  
  // Team Management Functions
  const handleAddAdmin = async () => {
    if (!newAdminEmail || !newAdminName) {
      toast.error('Email et nom requis');
      return;
    }
    
    setAddingAdmin(true);
    try {
      const res = await api.post('/admin/team/add', {
        email: newAdminEmail,
        name: newAdminName,
        phone: newAdminPhone || undefined
      });
      
      toast.success(res.data.message);
      toast.info(`Mot de passe: ${res.data.credentials.password}`);
      
      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminPhone('');
      setShowAddAdminModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur création admin');
    } finally {
      setAddingAdmin(false);
    }
  };
  
  const handleToggleAdmin = async (adminId) => {
    try {
      const res = await api.post(`/admin/team/${adminId}/toggle`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
            <Shield className="inline mr-2 text-[#E50914]" size={36} />
            Admin <span className="text-[#E50914]">Panel</span>
          </h1>
          <p className="text-zinc-400">Dashboard administrateur AGRP BOX</p>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('stats')}
            variant={activeTab === 'stats' ? 'default' : 'outline'}
            className={activeTab === 'stats' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <TrendingUp className="mr-2" size={18} />
            Stats
          </Button>
          <Button
            onClick={() => setActiveTab('titles')}
            variant={activeTab === 'titles' ? 'default' : 'outline'}
            className={activeTab === 'titles' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Music className="mr-2" size={18} />
            📊 SUIVI ENVOIS AUTO ({titles.length})
          </Button>
          <Button
            onClick={() => setActiveTab('artists')}
            variant={activeTab === 'artists' ? 'default' : 'outline'}
            className={activeTab === 'artists' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Users className="mr-2" size={18} />
            Artistes ({artists.length})
          </Button>
          <Button
            onClick={() => setActiveTab('transactions')}
            variant={activeTab === 'transactions' ? 'default' : 'outline'}
            className={activeTab === 'transactions' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <DollarSign className="mr-2" size={18} />
            Transactions ({transactions.length})
          </Button>
          <Button
            onClick={() => setActiveTab('live')}
            variant={activeTab === 'live' ? 'default' : 'outline'}
            className={activeTab === 'live' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Video className="mr-2" size={18} />
            🔴 LIVE STUDIO
          </Button>
          <Button
            onClick={() => setActiveTab('team')}
            variant={activeTab === 'team' ? 'default' : 'outline'}
            className={activeTab === 'team' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Shield className="mr-2" size={18} />
            👥 ÉQUIPE
          </Button>
          <Button
            onClick={() => setActiveTab('settings')}
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            className={activeTab === 'settings' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            ⚙️ Profil Global
          </Button>
          <Button
            onClick={() => setActiveTab('messages')}
            variant={activeTab === 'messages' ? 'default' : 'outline'}
            className={activeTab === 'messages' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Mail className="mr-2" size={18} />
            📧 Support ({contactMessages.length})
          </Button>
          <Button
            onClick={() => setActiveTab('distribution')}
            variant={activeTab === 'distribution' ? 'default' : 'outline'}
            className={activeTab === 'distribution' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            🎵 Distribution Audio
          </Button>
          <Button
            onClick={() => setActiveTab('dyvytv')}
            variant={activeTab === 'dyvytv' ? 'default' : 'outline'}
            className={activeTab === 'dyvytv' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            <Video className="mr-2" size={18} />
            📺 AGRP BOX TV
          </Button>
          <Button
            onClick={() => setActiveTab('gestion')}
            variant={activeTab === 'gestion' ? 'default' : 'outline'}
            className={activeTab === 'gestion' ? 'bg-red-600' : 'border-red-500'}
          >
            <Shield className="mr-2" size={18} />
            🗑️ GESTION ADMIN
          </Button>
          <Button
            onClick={() => setActiveTab('adsense')}
            variant={activeTab === 'adsense' ? 'default' : 'outline'}
            className={activeTab === 'adsense' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            💰 DEMANDES ADSENSE
            {adSenseApplications.filter(a => a.status === 'en_attente').length > 0 && (
              <span className="ml-2 bg-red-600 text-white text-xs rounded-full px-2 py-0.5">
                {adSenseApplications.filter(a => a.status === 'en_attente').length}
              </span>
            )}
          </Button>
          <Button
            onClick={() => setActiveTab('services')}
            variant={activeTab === 'services' ? 'default' : 'outline'}
            className={activeTab === 'services' ? 'bg-[#E50914]' : 'border-zinc-700'}
          >
            📦 COMMANDES SERVICES
            {serviceOrders.filter(o => o.delivery_status === 'en_attente' && o.payment_status === 'success').length > 0 && (
              <span className="ml-2 bg-red-600 text-white text-xs rounded-full px-2 py-0.5">
                {serviceOrders.filter(o => o.delivery_status === 'en_attente' && o.payment_status === 'success').length}
              </span>
            )}
          </Button>
        </div>
        
        {/* Stats Section */}
        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Card className="glass border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center">
                  <Users className="mr-2" size={16} />
                  Total Artistes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats?.total_artists || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="glass border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center">
                  <DollarSign className="mr-2" size={16} />
                  Audio 15$ Encaissés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-500">{stats?.total_15_collected || 0}$</div>
                <p className="text-xs text-zinc-500 mt-1">{stats?.count_15_payments || 0} paiements</p>
              </CardContent>
            </Card>
            
            <Card className="glass border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center">
                  <Video className="mr-2" size={16} />
                  Vidéo 18$ Encaissés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-500">{stats?.total_18_collected || 0}$</div>
                <p className="text-xs text-zinc-500 mt-1">{stats?.count_18_payments || 0} paiements</p>
              </CardContent>
            </Card>
            
            <Card className="glass border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center">
                  <Video className="mr-2" size={16} />
                  Vidéos Vendues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-500">{stats?.total_videos_sold || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="glass border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-400 flex items-center">
                  <TrendingUp className="mr-2" size={16} />
                  Commission 30%
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#F97316]">{stats?.commission_30_generated?.toFixed(2) || 0}$</div>
                <p className="text-xs text-zinc-500 mt-1">Artistes: {stats?.artist_revenue_70_total?.toFixed(2) || 0}$</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Titles Section - SUIVI ENVOIS AUTO (Lecture Seule) */}
        {activeTab === 'titles' && (
          <div className="space-y-4">
            <Card className="glass border-blue-800 border-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Clock className="mr-2 text-blue-500" size={20} />
                  📊 SUIVI ENVOIS AUTO - Lecture Seule
                </CardTitle>
                <p className="text-sm text-zinc-400">
                  Distribution automatique activée. Les titres sont envoyés automatiquement après paiement validé.
                </p>
              </CardHeader>
            </Card>
            
            {titles.length === 0 ? (
              <Card className="glass border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Music className="mx-auto mb-4 text-zinc-600" size={48} />
                  <p className="text-zinc-400">Aucun titre uploadé pour le moment</p>
                </CardContent>
              </Card>
            ) : (
              titles.map((title) => (
                <Card key={title.id} className="glass border-zinc-800">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-1">{title.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-zinc-400">
                          <span className="flex items-center">
                            <Users className="mr-1" size={14} />
                            {title.artist_name}
                          </span>
                          <span className="flex items-center">
                            <Mail className="mr-1" size={14} />
                            {title.artist_email}
                          </span>
                          <span className="text-[#F97316]">Paiement: 25$</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {title.status === 'publie_sur_spotify' ? (
                          <span className="flex items-center text-green-500 text-sm font-semibold">
                            <CheckCircle className="mr-1" size={16} />
                            ✅ EN LIGNE - Spotify/Apple/Boomplay
                          </span>
                        ) : title.status === 'envoye_auto' ? (
                          <span className="flex items-center text-blue-500 text-sm font-semibold">
                            <Clock className="mr-1" size={16} />
                            🚀 ENVOYÉ AUTO - En ligne dans 3-7 jours
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-500 text-sm font-semibold">
                            <Clock className="mr-1" size={16} />
                            ⏳ Paiement en attente
                          </span>
                        )}
                        
                        {/* BOUTON SUPPRIMER (Admin uniquement) */}
                        <Button
                          onClick={async () => {
                            if (!confirm(`Supprimer définitivement "${title.title}" ?`)) return;
                            
                            try {
                              await api.delete(`/admin/videos/${title.id}/force-delete`);
                              toast.success('✅ Vidéo supprimée !');
                              fetchAllData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Erreur suppression');
                            }
                          }}
                          variant="destructive"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          🗑️ SUPPRIMER
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
        
        {/* Artists Section */}
        {activeTab === 'artists' && (
          <div className="space-y-4">
            {artists.length === 0 ? (
              <Card className="glass border-zinc-800">
                <CardContent className="py-12 text-center">
                  <Users className="mx-auto mb-4 text-zinc-600" size={48} />
                  <p className="text-zinc-400">Aucun artiste inscrit</p>
                </CardContent>
              </Card>
            ) : (
              artists.map((artist) => (
                <Card key={artist.id} className="glass border-zinc-800">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-1">
                          {artist.name}
                          {artist.role === 'super_admin' && (
                            <span className="ml-2 text-xs bg-red-900 text-red-300 px-2 py-1 rounded">ADMIN</span>
                          )}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-zinc-400">
                          <span className="flex items-center">
                            <Mail className="mr-1" size={14} />
                            {artist.email}
                          </span>
                          <span className="flex items-center">
                            <Phone className="mr-1" size={14} />
                            {artist.phone}
                          </span>
                          <span className="flex items-center">
                            <Video className="mr-1" size={14} />
                            {artist.video_count} vidéos
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" className="border-zinc-700">
                          <Eye className="mr-1" size={14} />
                          Voir
                        </Button>
                        <Button
                          onClick={() => handleToggleArtistStatus(artist.id)}
                          variant={artist.is_active ? 'destructive' : 'default'}
                          size="sm"
                        >
                          <Ban className="mr-1" size={14} />
                          {artist.is_active ? 'Désactiver' : 'Activer'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
        
        {/* Transactions Section */}
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <Card className="glass border-zinc-800">
                <CardContent className="py-12 text-center">
                  <DollarSign className="mx-auto mb-4 text-zinc-600" size={48} />
                  <p className="text-zinc-400">Aucune transaction pour le moment</p>
                </CardContent>
              </Card>
            ) : (
              transactions.map((transaction) => (
                <Card key={transaction.id} className="glass border-zinc-800">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-1">
                          Paiement Distribution - {transaction.amount_usd}$
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-zinc-400">
                          <span className="flex items-center">
                            <Users className="mr-1" size={14} />
                            {transaction.artist_name}
                          </span>
                          <span className="flex items-center">
                            <Mail className="mr-1" size={14} />
                            {transaction.artist_email}
                          </span>
                          <span>{transaction.payment_method}</span>
                          <span>{new Date(transaction.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      
                      <div>
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          transaction.status === 'success' ? 'bg-green-900 text-green-300' :
                          transaction.status === 'pending' ? 'bg-yellow-900 text-yellow-300' :
                          'bg-red-900 text-red-300'
                        }`}>
                          {transaction.status === 'success' ? '✓ Payé' :
                           transaction.status === 'pending' ? '⏳ En attente' :
                           '✗ Échoué'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
        
        {/* LIVE STUDIO Section */}
        {activeTab === 'live' && (
          <div className="space-y-6">
            {/* Section Upload Vidéo */}
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Video className="mr-2 text-[#E50914]" size={24} />
                  📤 Upload Vidéo MP4
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Titre de la vidéo</Label>
                  <Input
                    value={uploadVideoTitle}
                    onChange={(e) => setUploadVideoTitle(e.target.value)}
                    placeholder="Ex: Concert Live Kinshasa 2025"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <Label>Fichier Vidéo (MP4)</Label>
                  <Input
                    type="file"
                    accept="video/mp4"
                    onChange={(e) => setUploadVideoFile(e.target.files[0])}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <Button
                  onClick={handleUploadVideo}
                  disabled={uploadingVideo}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg font-semibold"
                >
                  {uploadingVideo ? (
                    <Loader2 className="animate-spin mr-2" />
                  ) : (
                    '📤 UPLOAD VIDÉO'
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Section URL YouTube */}
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Video className="mr-2 text-red-600" size={24} />
                  📺 Ajouter URL YouTube
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Titre (optionnel)</Label>
                  <Input
                    value={youtubeTitle}
                    onChange={(e) => setYoutubeTitle(e.target.value)}
                    placeholder="Titre de la vidéo"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <Label>URL YouTube</Label>
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <Button
                  onClick={handleAddYoutube}
                  className="w-full bg-red-600 hover:bg-red-700 py-6 text-lg font-semibold"
                >
                  ➕ AJOUTER À LA FILE D'ATTENTE
                </Button>
              </CardContent>
            </Card>
            
            {/* Liste d'Attente */}
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Clock className="mr-2 text-[#F97316]" size={28} />
                  📋 LISTE D'ATTENTE ({liveQueue.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {liveQueue.length === 0 ? (
                  <div className="py-12 text-center">
                    <Video className="mx-auto mb-4 text-zinc-600" size={48} />
                    <p className="text-zinc-400">Aucune vidéo dans la file d'attente</p>
                    <p className="text-zinc-500 text-sm mt-2">Uploadez une vidéo ou ajoutez une URL YouTube</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {liveQueue.map((video) => (
                      <div
                        key={video.id}
                        className={`p-4 rounded-lg border-2 ${
                          video.status === 'live' 
                            ? 'border-red-600 bg-red-900/20' 
                            : 'border-zinc-700 bg-zinc-900'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-bold text-white text-lg">{video.title}</h3>
                              {video.status === 'live' && (
                                <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-bold animate-pulse">
                                  🔴 EN DIRECT
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-zinc-400">
                              <span className="px-2 py-1 bg-zinc-800 rounded">
                                {video.type === 'youtube' ? '📺 YouTube' : '📤 Upload'}
                              </span>
                              <span>{new Date(video.created_at).toLocaleString('fr-FR')}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {video.status !== 'live' && (
                              <Button
                                onClick={() => handleLaunchLive(video.id)}
                                className="bg-red-600 hover:bg-red-700 px-6 py-6 text-lg font-bold"
                              >
                                🔴 LANCER EN DIRECT
                              </Button>
                            )}
                            <Button
                              onClick={() => handleDeleteLiveVideo(video.id)}
                              variant="destructive"
                              size="sm"
                            >
                              🗑️
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* TEAM Section */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            {/* Header avec bouton Ajouter */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">Gestion de l'Équipe Admin</h2>
                <p className="text-zinc-400 mt-1">Gérez les administrateurs de la plateforme</p>
              </div>
              <Button
                onClick={() => setShowAddAdminModal(true)}
                className="bg-green-600 hover:bg-green-700 px-6 py-6 text-lg font-semibold"
              >
                ➕ AJOUTER UN ADMIN
              </Button>
            </div>
            
            {/* Modal Ajouter Admin */}
            {showAddAdminModal && (
              <Card className="glass border-[#E50914] border-2">
                <CardHeader>
                  <CardTitle className="text-xl">Créer un Admin Limité</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Nom complet *</Label>
                    <Input
                      value={newAdminName}
                      onChange={(e) => setNewAdminName(e.target.value)}
                      placeholder="Ex: Jean Mukendi"
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      placeholder="admin@agrpbox.com"
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                  <div>
                    <Label>Téléphone (optionnel)</Label>
                    <Input
                      type="tel"
                      value={newAdminPhone}
                      onChange={(e) => setNewAdminPhone(e.target.value)}
                      placeholder="+243..."
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                  
                  <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Rôle : Admin Limité</h4>
                    <div className="space-y-1 text-sm">
                      <p className="text-green-400">✅ Voir Stats & Artistes</p>
                      <p className="text-green-400">✅ Valider Titres</p>
                      <p className="text-green-400">✅ Upload Vidéo Live</p>
                      <p className="text-red-400">❌ Supprimer Artistes/Titres</p>
                      <p className="text-red-400">❌ Voir Transactions</p>
                      <p className="text-red-400">❌ Modifier Prix/Commissions</p>
                      <p className="text-red-400">❌ Gérer Équipe Admin</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button
                      onClick={handleAddAdmin}
                      disabled={addingAdmin}
                      className="flex-1 bg-green-600 hover:bg-green-700 py-6"
                    >
                      {addingAdmin ? (
                        <Loader2 className="animate-spin mr-2" />
                      ) : (
                        '✅ CRÉER ADMIN LIMITÉ'
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowAddAdminModal(false);
                        setNewAdminEmail('');
                        setNewAdminName('');
                        setNewAdminPhone('');
                      }}
                      variant="outline"
                      className="border-zinc-700"
                    >
                      Annuler
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Liste des Admins */}
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="text-xl">👥 Équipe Administrative ({adminTeam.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {adminTeam.length === 0 ? (
                  <div className="py-12 text-center">
                    <Shield className="mx-auto mb-4 text-zinc-600" size={48} />
                    <p className="text-zinc-400">Aucun administrateur</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {adminTeam.map((admin) => (
                      <div
                        key={admin.id}
                        className={`p-4 rounded-lg border ${
                          admin.role === 'super_admin'
                            ? 'border-red-600 bg-red-900/20'
                            : 'border-zinc-700 bg-zinc-900'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-bold text-white text-lg">{admin.name}</h3>
                              {admin.role === 'super_admin' ? (
                                <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-bold">
                                  👑 SUPER ADMIN
                                </span>
                              ) : (
                                <span className="px-3 py-1 bg-blue-900 text-blue-300 rounded-full text-xs font-bold">
                                  🛡️ ADMIN LIMITÉ
                                </span>
                              )}
                              {!admin.is_active && (
                                <span className="px-3 py-1 bg-zinc-700 text-zinc-400 rounded-full text-xs">
                                  ⏸️ DÉSACTIVÉ
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-zinc-400">
                              <span className="flex items-center">
                                <Mail className="mr-1" size={14} />
                                {admin.email}
                              </span>
                              <span className="flex items-center">
                                <Phone className="mr-1" size={14} />
                                {admin.phone}
                              </span>
                              <span>{new Date(admin.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                          
                          <div>
                            {admin.role !== 'super_admin' && (
                              <Button
                                onClick={() => handleToggleAdmin(admin.id)}
                                variant={admin.is_active ? 'destructive' : 'default'}
                                size="sm"
                              >
                                {admin.is_active ? '⏸️ DÉSACTIVER' : '▶️ ACTIVER'}
                              </Button>
                            )}
                            {admin.role === 'super_admin' && (
                              <span className="text-zinc-500 text-sm">🔒 Protégé</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Profil Global Section */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  ⚙️ Paramètres Globaux de la Plateforme
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setSavingSettings(true);
                  
                  try {
                    const formData = new FormData();
                    formData.append('platform_name', e.target.platform_name.value);
                    formData.append('contact_email', e.target.contact_email.value);
                    formData.append('contact_phone', e.target.contact_phone.value);
                    
                    if (logoFile) formData.append('logo', logoFile);
                    if (bannerFile) formData.append('banner', bannerFile);
                    if (profilePhotoFile) formData.append('profile_photo', profilePhotoFile);
                    
                    await api.put('/admin/global-settings', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    
                    toast.success('✅ Paramètres mis à jour !');
                    fetchAllData();
                    setLogoFile(null);
                    setBannerFile(null);
                    setProfilePhotoFile(null);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Erreur de sauvegarde');
                  } finally {
                    setSavingSettings(false);
                  }
                }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nom de la plateforme</Label>
                      <Input
                        name="platform_name"
                        defaultValue={globalSettings?.platform_name}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label>Email de contact</Label>
                      <Input
                        name="contact_email"
                        type="email"
                        defaultValue={globalSettings?.contact_email}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label>Téléphone de contact</Label>
                      <Input
                        name="contact_phone"
                        type="tel"
                        defaultValue={globalSettings?.contact_phone}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div>
                      <Label>Logo de la plateforme</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLogoFile(e.target.files[0])}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                      {globalSettings?.logo_url && (
                        <p className="text-xs text-green-500 mt-1">✓ Logo actuel configuré</p>
                      )}
                    </div>
                    
                    <div>
                      <Label>Bannière de la plateforme</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setBannerFile(e.target.files[0])}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                      {globalSettings?.banner_url && (
                        <p className="text-xs text-green-500 mt-1">✓ Bannière actuelle configurée</p>
                      )}
                    </div>
                    
                    <div>
                      <Label>Photo de profil Admin</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProfilePhotoFile(e.target.files[0])}
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                      {globalSettings?.admin_profile_photo && (
                        <p className="text-xs text-green-500 mt-1">✓ Photo actuelle configurée</p>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={savingSettings}
                    className="w-full bg-[#E50914] hover:bg-[#F8212C] mt-6"
                  >
                    {savingSettings ? <Loader2 className="animate-spin" /> : '💾 Sauvegarder les modifications'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Messages de Contact/Support Section */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Mail className="mr-2 text-[#E50914]" />
                  📧 Messages de Contact / Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contactMessages.length === 0 ? (
                  <p className="text-zinc-400 text-center py-8">Aucun message pour le moment</p>
                ) : (
                  <div className="space-y-4">
                    {contactMessages.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`border rounded-lg p-4 ${
                          msg.status === 'unread' 
                            ? 'border-[#E50914] bg-red-900/10' 
                            : 'border-zinc-800 bg-zinc-900/30'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-semibold text-lg text-white">{msg.subject}</h3>
                            <p className="text-sm text-zinc-400">
                              De: {msg.name} ({msg.email})
                              {msg.phone && ` • ${msg.phone}`}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {new Date(msg.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {msg.status === 'unread' && (
                              <span className="px-3 py-1 bg-[#E50914] text-white rounded-full text-xs font-semibold">
                                🔔 NOUVEAU
                              </span>
                            )}
                            {msg.status === 'read' && (
                              <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs">
                                👁️ LU
                              </span>
                            )}
                            {msg.status === 'replied' && (
                              <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs">
                                ✅ RÉPONDU
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-zinc-900 rounded-lg p-3 mb-3">
                          <p className="text-white whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await api.put(`/admin/contact-messages/${msg.id}/status`, null, {
                                  params: { status: 'read' }
                                });
                                toast.success('Marqué comme lu');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Erreur');
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            👁️ Marquer comme lu
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await api.put(`/admin/contact-messages/${msg.id}/status`, null, {
                                  params: { status: 'replied' }
                                });
                                toast.success('Marqué comme répondu');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Erreur');
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            ✅ Marquer comme répondu
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Distribution Audio Section */}
        {activeTab === 'distribution' && (
          <div className="space-y-6">
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Music className="mr-2 text-[#E50914]" />
                  🎵 Gestion de la Distribution Audio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-400 mb-6">
                  Marquez les audios comme "Distribués" une fois que vous avez terminé la distribution manuelle sur Spotify, Apple Music, etc.
                </p>
                
                {titles.filter(t => t.file_type === 'mp3').length === 0 ? (
                  <p className="text-zinc-400 text-center py-8">Aucun audio en attente de distribution</p>
                ) : (
                  <div className="space-y-4">
                    {titles.filter(t => t.file_type === 'mp3').map((audio) => (
                      <div key={audio.id} className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/30">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-white">{audio.title}</h3>
                            <p className="text-sm text-zinc-400">Artiste: {audio.artist_name}</p>
                            <p className="text-xs text-zinc-500">
                              Uploadé le: {new Date(audio.created_at).toLocaleString('fr-FR')}
                            </p>
                            
                            <div className="mt-3 flex flex-wrap gap-2">
                              <span className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs">
                                Spotify
                              </span>
                              <span className="px-2 py-1 bg-pink-900 text-pink-300 rounded text-xs">
                                Apple Music
                              </span>
                              <span className="px-2 py-1 bg-orange-900 text-orange-300 rounded text-xs">
                                Deezer
                              </span>
                              <span className="px-2 py-1 bg-red-900 text-red-300 rounded text-xs">
                                YouTube Music
                              </span>
                              <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded text-xs">
                                Amazon Music
                              </span>
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <Button
                              onClick={async () => {
                                try {
                                  await api.post(`/admin/audio-distribution/${audio.id}/mark-distributed`);
                                  toast.success('✅ Audio marqué comme distribué ! Compte à rebours de 3 jours activé.');
                                  fetchAllData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Erreur');
                                }
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              ✅ Marquer comme DISTRIBUÉ
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* AGRP BOX TV Manuelle Section */}
        {activeTab === 'dyvytv' && (
          <div className="space-y-6">
            {/* Statut actuel du live */}
            <Card className="glass border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Video className="mr-2 text-[#E50914]" />
                  📺 AGRP BOX TV - Contrôle Manuel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-white">Statut actuel</h3>
                  {currentLiveStatus?.is_live ? (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-2xl font-bold text-red-500">🔴 EN DIRECT</span>
                      </div>
                      <p className="text-white text-xl">{currentLiveStatus.video.title}</p>
                      <p className="text-zinc-400 text-sm">
                        Lancé le : {new Date(currentLiveStatus.video.launched_at).toLocaleString('fr-FR')}
                      </p>
                      <Button
                        onClick={async () => {
                          try {
                            setLaunchingLive(true);
                            await api.post('/admin/dyvy-tv/stop');
                            toast.success('⏹️ LIVE COUPÉ !');
                            fetchAllData();
                          } catch (error) {
                            toast.error('Erreur arrêt live');
                          } finally {
                            setLaunchingLive(false);
                          }
                        }}
                        disabled={launchingLive}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 text-lg"
                      >
                        {launchingLive ? <Loader2 className="animate-spin" /> : '⏹️ COUPER LE LIVE'}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-zinc-400 text-lg">⚫ Aucun live en cours</p>
                      <p className="text-zinc-500 text-sm mt-2">Uploadez une vidéo et lancez-la quand vous êtes prêt</p>
                    </div>
                  )}
                </div>

                {/* Upload nouvelle vidéo */}
                <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-white">📤 Uploader une vidéo pour le live</h3>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!agrpBoxTvFile) {
                      toast.error('Sélectionnez une vidéo');
                      return;
                    }

                    setUploadingAgrpBoxTv(true);
                    try {
                      const formData = new FormData();
                      formData.append('title', agrpBoxTvTitle);
                      formData.append('video_file', agrpBoxTvFile);

                      await api.post('/admin/dyvy-tv/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });

                      toast.success('✅ Vidéo uploadée et prête !');
                      setAgrpBoxTvTitle('');
                      setAgrpBoxTvFile(null);
                      fetchAllData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Erreur upload');
                    } finally {
                      setUploadingAgrpBoxTv(false);
                    }
                  }} className="space-y-4">
                    <div>
                      <Label>Titre de la vidéo *</Label>
                      <Input
                        value={agrpBoxTvTitle}
                        onChange={(e) => setAgrpBoxTvTitle(e.target.value)}
                        required
                        placeholder="Ex: Concert Live de Raoul MLELO"
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                    </div>

                    <div>
                      <Label>Vidéo MP4 *</Label>
                      <Input
                        type="file"
                        accept="video/mp4"
                        onChange={(e) => setAgrpBoxTvFile(e.target.files[0])}
                        required
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Format MP4, max 500MB</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={uploadingAgrpBoxTv}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {uploadingAgrpBoxTv ? <Loader2 className="animate-spin" /> : '📤 UPLOADER LA VIDÉO'}
                    </Button>
                  </form>
                </div>

                {/* Vidéos prêtes à être lancées */}
                <div className="bg-zinc-900 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-white">
                    🎬 Vidéos prêtes ({readyVideos.length})
                  </h3>
                  
                  {readyVideos.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">
                      Aucune vidéo en attente. Uploadez-en une ci-dessus.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {readyVideos.map((video, index) => (
                        <div key={index} className="border border-zinc-800 rounded-lg p-4 bg-black/30">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <h4 className="font-semibold text-white text-lg">{video.title}</h4>
                              <p className="text-zinc-400 text-sm">
                                Uploadée le : {new Date(video.created_at).toLocaleString('fr-FR')}
                              </p>
                              <span className="inline-block mt-2 px-3 py-1 bg-green-900 text-green-300 rounded text-xs font-semibold">
                                ✅ PRÊTE
                              </span>
                            </div>
                            
                            <Button
                              onClick={async () => {
                                if (!confirm(`Lancer le live "${video.title}" maintenant ?`)) return;
                                
                                try {
                                  setLaunchingLive(true);
                                  await api.post(`/admin/dyvy-tv/launch/${video.video_id}`);
                                  toast.success('🔴 LIVE LANCÉ ! Tous les clients le voient maintenant.');
                                  fetchAllData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Erreur lancement');
                                } finally {
                                  setLaunchingLive(false);
                                }
                              }}
                              disabled={launchingLive || currentLiveStatus?.is_live}
                              className="bg-[#E50914] hover:bg-[#F8212C] text-white font-bold py-3 px-6 text-base"
                            >
                              {launchingLive ? <Loader2 className="animate-spin" /> : '🔴 PASSER EN DIRECT'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {/* GESTION ADMIN Section - SUPER ADMIN UNIQUEMENT */}
        {activeTab === 'gestion' && (
          <div className="space-y-6">
            <Card className="glass border-red-800 border-2">
              <CardHeader>
                <CardTitle className="flex items-center text-3xl text-red-500">
                  <Shield className="mr-3" size={32} />
                  🗑️ GESTION ADMIN - DANIEL UNIQUEMENT
                </CardTitle>
                <p className="text-zinc-300 text-base mt-3">
                  ⚠️ <strong>SUPER ADMIN</strong> : Vous voyez TOUTES les vidéos de TOUS les artistes.
                  <br />
                  Vous pouvez SUPPRIMER n'importe quelle vidéo et MODIFIER les prix.
                </p>
              </CardHeader>
              <CardContent>
                <div className="bg-zinc-900 rounded-lg p-5 mb-6 border border-red-800">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white font-bold text-xl">📊 Total vidéos : {allVideos.length}</p>
                      <p className="text-zinc-400 text-sm mt-1">
                        Cliquez sur "🗑️ SUPPRIMER" pour retirer définitivement une vidéo
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        if (!confirm('Mettre TOUTES les vidéos à 7$ ?')) return;
                        
                        try {
                          for (const video of allVideos) {
                            await api.put(`/admin/videos/${video.id}/price`, null, {
                              params: { new_price: 7 }
                            });
                          }
                          toast.success('✅ Tous les prix mis à 7$ !');
                          fetchAllData();
                        } catch (error) {
                          toast.error('Erreur modification prix');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6"
                    >
                      💰 TOUT METTRE À 7$
                    </Button>
                  </div>
                </div>

                {allVideos.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="mx-auto mb-4 text-zinc-600" size={64} />
                    <p className="text-zinc-400 text-lg">Aucune vidéo dans la boutique</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allVideos.map((video, index) => (
                      <Card key={video.id || index} className="glass border-zinc-800 hover:border-red-500 transition-all">
                        <CardContent className="py-5">
                          <div className="flex items-center justify-between gap-6">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-zinc-500 font-mono text-sm">#{index + 1}</span>
                                <h3 className="font-bold text-white text-xl">{video.title || 'Sans titre'}</h3>
                              </div>
                              <div className="flex items-center flex-wrap gap-4 text-sm">
                                <span className="text-zinc-300 bg-zinc-800 px-3 py-1 rounded">
                                  👤 {video.artist_name || 'Inconnu'}
                                </span>
                                <span className="text-[#F97316] font-bold bg-orange-900/30 px-3 py-1 rounded">
                                  💰 {video.price_usd || 0}$
                                </span>
                                <span className="text-zinc-400 bg-zinc-800 px-3 py-1 rounded">
                                  📁 {video.file_type || 'mp4'}
                                </span>
                                <span className="text-zinc-400 bg-zinc-800 px-3 py-1 rounded">
                                  👁️ {video.views || 0} vues
                                </span>
                                <span className="text-zinc-400 bg-zinc-800 px-3 py-1 rounded">
                                  🛒 {video.purchases || 0} achats
                                </span>
                                <span className="text-red-400 bg-zinc-800 px-3 py-1 rounded">
                                  ❤️ {video.likes || 0}
                                </span>
                              </div>
                            </div>

                            {/* BOUTONS ACTION */}
                            <div className="flex items-center gap-3">
                              {/* Bouton modifier prix */}
                              <Button
                                onClick={async () => {
                                  const newPrice = prompt(`Prix actuel: ${video.price_usd}$\n\nNouveau prix (1-100$):`, '7');
                                  if (!newPrice || newPrice < 1 || newPrice > 100) return;
                                  
                                  try {
                                    await api.put(`/admin/videos/${video.id}/price`, null, {
                                      params: { new_price: parseInt(newPrice) }
                                    });
                                    toast.success(`✅ Prix changé à ${newPrice}$`);
                                    fetchAllData();
                                  } catch (error) {
                                    toast.error('Erreur modification prix');
                                  }
                                }}
                                size="lg"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-5"
                              >
                                💰 PRIX
                              </Button>
                              
                              {/* Bouton supprimer */}
                              <Button
                                onClick={async () => {
                                  if (!confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ?\n\n"${video.title}"\n\nCette action est IRRÉVERSIBLE.`)) {
                                    return;
                                  }

                                  try {
                                    await api.delete(`/admin/videos/${video.id}/force-delete`);
                                    toast.success(`✅ "${video.title}" supprimée !`);
                                    fetchAllData();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Erreur suppression');
                                    console.error(error);
                                  }
                                }}
                                variant="destructive"
                                size="lg"
                                className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 text-base"
                              >
                                🗑️ SUPPRIMER
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ADSENSE - Gestion des demandes AdSense */}
        {activeTab === 'adsense' && (
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign size={24} />
                💰 Demandes de Validation Google AdSense
              </CardTitle>
              <p className="text-zinc-400 text-sm">
                Gérez les demandes de validation AdSense des créateurs
              </p>
            </CardHeader>
            <CardContent>
              {adSenseApplications.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-zinc-400">Aucune demande pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {adSenseApplications.map((app, index) => (
                    <Card key={index} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="py-4">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-white text-lg">{app.full_name}</h3>
                              <p className="text-sm text-zinc-400">{app.user_name} - {app.user_phone}</p>
                              <p className="text-sm text-zinc-400 mt-1">📍 {app.address}, {app.country}</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-3 py-1 rounded text-sm font-medium ${
                                app.status === 'en_attente' ? 'bg-yellow-900/30 text-yellow-400' :
                                app.status === 'en_cours' ? 'bg-blue-900/30 text-blue-400' :
                                'bg-green-900/30 text-green-400'
                              }`}>
                                {app.status === 'en_attente' ? '⏳ En attente' :
                                 app.status === 'en_cours' ? '🔄 En cours' : '✅ Envoyé'}
                              </span>
                              <p className="text-xs text-zinc-500 mt-2">
                                {new Date(app.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-zinc-800 rounded p-3">
                            <Label className="text-zinc-400 text-xs">Passeport :</Label>
                            <p className="text-white text-sm font-mono">{app.passport_file_path}</p>
                          </div>
                          
                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => handleReviewAdSense(app.user_id, 'en_attente')}
                              disabled={reviewingAdSense || app.status === 'en_attente'}
                              className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                            >
                              ⏳ EN ATTENTE
                            </Button>
                            <Button
                              onClick={() => handleReviewAdSense(app.user_id, 'en_cours', 'Création du compte en cours...')}
                              disabled={reviewingAdSense || app.status === 'en_cours'}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              🔄 EN COURS
                            </Button>
                            <Button
                              onClick={() => handleReviewAdSense(app.user_id, 'envoye', 'Identifiants AdSense envoyés au client')}
                              disabled={reviewingAdSense || app.status === 'envoye'}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              ✅ ENVOYÉ
                            </Button>
                          </div>
                          
                          {app.admin_note && (
                            <div className="bg-zinc-800 rounded p-3 border-l-4 border-blue-500">
                              <Label className="text-zinc-400 text-xs">Note Admin :</Label>
                              <p className="text-white text-sm mt-1">{app.admin_note}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SERVICES - Gestion des commandes de services */}
        {activeTab === 'services' && (
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video size={24} />
                📦 Commandes de Services ({serviceOrders.length})
              </CardTitle>
              <p className="text-zinc-400 text-sm">
                Gérez les commandes de services professionnels (YouTube, VEVO, Distribution, etc.)
              </p>
            </CardHeader>
            <CardContent>
              {serviceOrders.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-zinc-400">Aucune commande pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {serviceOrders.map((order, index) => (
                    <Card key={index} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="py-4">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-white text-lg flex items-center gap-2">
                                {order.service_label}
                                {order.service_type === 'monetized_youtube' && ' 📺'}
                                {order.service_type === 'vevo_channel' && ' 🎵'}
                                {order.service_type === 'music_distribution' && ' 🎧'}
                              </h3>
                              <p className="text-sm text-zinc-400">{order.user_name} - {order.user_phone}</p>
                              <p className="text-lg font-bold text-[#F97316] mt-2">${order.amount_usd}</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-3 py-1 rounded text-sm font-medium ${
                                order.delivery_status === 'en_attente' ? 'bg-yellow-900/30 text-yellow-400' :
                                order.delivery_status === 'en_cours' ? 'bg-blue-900/30 text-blue-400' :
                                'bg-green-900/30 text-green-400'
                              }`}>
                                {order.delivery_status === 'en_attente' ? '⏳ En attente' :
                                 order.delivery_status === 'en_cours' ? '🔄 En cours' : '✅ Envoyé'}
                              </span>
                              <p className="text-xs text-zinc-500 mt-2">
                                {new Date(order.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-zinc-800 rounded p-3 flex items-center justify-between">
                            <div>
                              <Label className="text-zinc-400 text-xs">Paiement :</Label>
                              <p className="text-white text-sm">{order.payment_method} - {order.payment_status}</p>
                            </div>
                            {order.bkapay_reference && (
                              <p className="text-xs text-zinc-500 font-mono">{order.bkapay_reference}</p>
                            )}
                          </div>
                          
                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => handleUpdateServiceOrder(order.user_id, 'en_attente')}
                              disabled={updatingOrder || order.delivery_status === 'en_attente'}
                              className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                            >
                              ⏳ EN ATTENTE
                            </Button>
                            <Button
                              onClick={() => handleUpdateServiceOrder(order.user_id, 'en_cours', 'Service en cours de préparation...')}
                              disabled={updatingOrder || order.delivery_status === 'en_cours'}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              🔄 EN COURS
                            </Button>
                            <Button
                              onClick={() => handleUpdateServiceOrder(order.user_id, 'envoye', 'Accès envoyés au client (Email + Mot de passe)')}
                              disabled={updatingOrder || order.delivery_status === 'envoye'}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              ✅ ENVOYÉ
                            </Button>
                          </div>
                          
                          {order.admin_note && (
                            <div className="bg-zinc-800 rounded p-3 border-l-4 border-blue-500">
                              <Label className="text-zinc-400 text-xs">Note Admin :</Label>
                              <p className="text-white text-sm mt-1">{order.admin_note}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* PAYS - Gestion des pays autorisés */}
        {activeTab === 'pays' && (
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe size={24} />
                🌍 Gestion des Pays Autorisés
              </CardTitle>
              <p className="text-zinc-400 text-sm">Cochez les pays autorisés pour l'inscription. Si aucun pays coché = tous autorisés.</p>
            </CardHeader>
            <CardContent>
              <div className="bg-zinc-900 p-4 rounded mb-4">
                <p className="text-white font-semibold">{enabledCountries.length} pays sélectionnés</p>
                <p className="text-zinc-400 text-sm">Cliquez sur "Sauvegarder" après vos modifications</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6 max-h-96 overflow-y-auto p-4 bg-zinc-900 rounded">
                {COUNTRIES.map(country => (
                  <label key={country.code} className="flex items-center gap-2 cursor-pointer hover:bg-zinc-800 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={enabledCountries.includes(country.code)}
                      onChange={() => handleToggleCountry(country.code)}
                      className="w-4 h-4"
                    />
                    <span className="text-white text-sm">{country.name}</span>
                  </label>
                ))}
              </div>
              
              <Button onClick={handleSaveCountries} disabled={savingCountries} className="bg-green-600 hover:bg-green-700">
                {savingCountries ? 'Sauvegarde...' : '💾 Sauvegarder les Pays'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
